import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Building,
  BadgeCheck,
  MapPinned,
  HardHat,
  ShieldCheck,
  Mail,
  Phone,
  PackageCheck,
  ClipboardCheck,
  Wrench,
} from 'lucide-react';
import './Home.css';
import HSWorksGallery from '../components/HSWorksGallery';

const Home = () => {
  const productsGridRef = useRef(null);

  useEffect(() => {
    const grid = productsGridRef.current;
    if (!grid) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      grid.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            grid.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -80px 0px' }
    );

    observer.observe(grid);

    return () => observer.disconnect();
  }, []);

  const products = [
    {
      title: '看板・サイン',
      description:
        '店舗看板から施設サイン、案内表示まで。屋外広告物の許可申請が必要な設置にも対応します。',
      path: '/products/signage',
      image: '/images/services/signage.png',
    },
    {
      title: 'LED照明',
      description:
        '店舗・施設・屋外空間の照明を、明るさや消費電力まで含めてご提案します。',
      path: '/products/led-lighting',
      image: '/images/services/led-lighting.png',
    },
    {
      title: '緊急出動警報板',
      description:
        '消防署など緊急車両の出動施設向けに、出動時の注意喚起と周辺案内を行う警報板です。',
      path: '/products/emergency-board',
      image: '/images/services/emergency-board.png',
    },
    {
      title: 'LEDビジョン',
      description:
        '屋内外の映像演出・広告表示に対応。設置後の輝度調整や機器保守も行います。',
      path: '/products/led-vision',
      image: '/images/services/led-vision.png',
    },
    {
      title: '防犯カメラ',
      description:
        '小規模店舗の防犯対策から、工場・広域施設の統合監視まで対応します。',
      path: '/products/security-camera',
      image: '/images/services/security-camera.png',
    },
    {
      title: 'パーキングシステム',
      description:
        '満車・空車が分かりにくい駐車場の課題を、案内表示・満空表示で解決します。',
      path: '/products/parking-system',
      image: '/images/services/parking.png',
    },
    {
      title: '電光掲示板',
      description:
        '案内・誘導・注意喚起など、目的に応じた表示内容とサイズを一から製作します。',
      path: '/products/electric-board',
      image: '/images/services/electric-board.png',
    },
    {
      title: 'カスタム金物製作',
      description:
        '規格品では対応できない架台・ブラケット・取付金物を、現場条件に合わせて製作します。',
      path: '/products/custom-metal',
      image: '/images/services/custom-metal.png',
    },
  ];

  const supportScope = [
    {
      title: '現地確認',
      description: '設置場所・視認性・電源・配線経路などを確認します。',
      Icon: MapPinned,
    },
    {
      title: '製品選定',
      description: '用途・設置環境・ご予算に合わせて必要な製品を整理します。',
      Icon: BadgeCheck,
    },
    {
      title: '製作・施工',
      description: '看板・LED・金物・表示設備など、現場に合わせて対応します。',
      Icon: HardHat,
    },
    {
      title: '保守対応',
      description: '設置後の点検・修理・交換・追加設置もご相談いただけます。',
      Icon: ShieldCheck,
    },
  ];

  const reasons = [
    {
      number: '01',
      title: '現場条件に合わせたご提案',
      description:
        '設置場所・視認性・電源・配線経路などを確認し、現場に合った仕様をご提案します。',
    },
    {
      number: '02',
      title: '製作から施工まで一貫対応',
      description:
        '看板・LED・金物・表示機器など、必要な内容をまとめてご相談いただけます。',
    },
    {
      number: '03',
      title: '許可・登録に基づく対応',
      description:
        '建設業許可・屋外広告業登録に基づき、関連工事や申請対応まで含めて進めます。',
    },
    {
      number: '04',
      title: '設置後も相談できる安心感',
      description:
        '修理・交換・追加設置など、導入後のご相談にも継続して対応します。',
    },
  ];

  const flow = [
    {
      step: '01',
      title: 'お問い合わせ・ご相談',
      description: 'ご要望や設置予定内容をお伺いします。',
      image: '/images/process-consultation.jpg',
    },
    {
      step: '02',
      title: '現地確認・ヒアリング',
      description: '設置場所や周辺環境、運用条件を確認します。',
      image: '/images/process-site-survey.jpg',
    },
    {
      step: '03',
      title: 'ご提案・お見積り',
      description: '内容に合わせた仕様と費用をご案内します。',
      image: '/images/process-proposal.jpg',
    },
    {
      step: '04',
      title: '設計・製作',
      description: '仕様を整理し、必要な製作を進めます。',
      image: '/images/process-design.jpg',
    },
    {
      step: '05',
      title: '施工・設置',
      description: '安全に配慮し、丁寧に施工します。',
      image: '/images/process-installation.jpg',
    },
    {
      step: '06',
      title: '保守対応',
      description: '設置後の点検・修理・追加対応も承ります。',
      image: '/images/process-maintenance.jpg',
    },
  ];

  return (
    <div className="home">
      {/* New left-aligned HS hero. Header and all following homepage sections remain unchanged. */}
      <section className="hs-real-hero" data-testid="home-hero" aria-labelledby="hs-real-hero-title">
        <div className="hs-real-hero__photo" aria-hidden="true" />
        <div className="hs-real-hero__overlay" aria-hidden="true" />

        <div className="hs-real-hero__container">
          <div className="hs-real-hero__content">
            <p className="hs-real-hero__eyebrow">PRODUCTS &amp; SOLUTIONS</p>
            <h1 className="hs-real-hero__title" id="hs-real-hero-title">
              製品の選定から、施工・保守まで。
            </h1>
            <div className="hs-real-hero__product-focus" aria-label="主な取扱製品">
              <p className="hs-real-hero__products">
                看板・LED照明・LEDビジョン<br />
                防犯カメラ・パーキングシステム
              </p>
              <p className="hs-real-hero__priority-product">緊急出動警報板</p>
            </div>
            <p className="hs-real-hero__intro">
              用途や設置条件に合わせた製品をご提案し、<br className="hs-real-hero__desktop-break" />
              製作・施工から設置後の保守まで対応しています。
            </p>

            <div className="hs-real-hero__capabilities" aria-label="対応内容">
              <div className="hs-real-hero__capability">
                <PackageCheck aria-hidden="true" size={25} strokeWidth={1.65} />
                <span>製品提案</span>
              </div>
              <div className="hs-real-hero__capability">
                <ClipboardCheck aria-hidden="true" size={25} strokeWidth={1.65} />
                <span>現地確認</span>
              </div>
              <div className="hs-real-hero__capability">
                <HardHat aria-hidden="true" size={25} strokeWidth={1.65} />
                <span>施工・設置</span>
              </div>
              <div className="hs-real-hero__capability">
                <Wrench aria-hidden="true" size={25} strokeWidth={1.65} />
                <span>保守対応</span>
              </div>
            </div>

            <div className="hs-real-hero__actions">
              <a className="hs-real-hero__button hs-real-hero__button--primary" href="#products">
                製品・サービスを見る
                <ArrowRight aria-hidden="true" size={17} strokeWidth={1.8} />
              </a>
              <Link className="hs-real-hero__button hs-real-hero__button--secondary" to="/contact">
                お問い合わせ
                <ArrowRight aria-hidden="true" size={17} strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section products-section" id="products">
        <div className="products-container-wide">
          <div className="section-head">
            <span className="section-eyebrow">PRODUCTS & SERVICES</span>
            <h2 className="section-h2">取扱製品・サービス</h2>
            <p className="section-lead">
              設置場所や用途に合わせて、必要な製品・設備をお選びいただけます。
            </p>
          </div>

          <div className="home-products-grid" ref={productsGridRef}>
            {products.map((product, index) => (
              <Link
                to={product.path}
                key={product.title}
                className="home-product-card"
                data-testid={`product-panel-${index}`}
                style={{ '--i': index }}
              >
                <div className="home-product-image">
                  <img src={product.image} alt={product.title} loading="lazy" />
                  <span className="home-product-number">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="home-product-body">
                  <h3>{product.title}</h3>
                  <p>{product.description}</p>

                  <span className="home-product-cta">
                    詳細を見る
                    <ArrowRight size={14} strokeWidth={2} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HSWorksGallery />

      <section className="home-section reasons-section" id="strengths">
        <div
          className="reasons-bg-image"
          style={{ backgroundImage: "url('/images/backgrounds/reasons-bg.png')" }}
          aria-hidden="true"
        />

        <div className="container">
          <div className="section-head reasons-head">
            <span className="section-eyebrow">STRENGTH</span>
            <h2 className="section-h2">選ばれる理由</h2>
            <p className="section-lead">
              現地確認から製作・施工・保守まで、必要な内容を一つずつ整理しながら対応します。
            </p>
          </div>

          <div className="reasons-grid reasons-grid-centered">
            {reasons.map((reason) => (
              <div className="reason-item" key={reason.title}>
                <span className="reason-number">{reason.number}</span>
                <h3>{reason.title}</h3>
                <p>{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section support-scope-section">
        <div className="container">
          <div className="section-head support-scope-title">
            <span className="section-eyebrow">SERVICE SCOPE</span>
            <h2 className="section-h2">現地確認から施工後のご相談まで</h2>
            <p className="section-lead">
              製品の販売だけでなく、設置環境や運用方法を確認しながら、現場に必要な内容を整理してご提案します。
            </p>
          </div>

          <div className="support-scope-grid support-scope-grid-centered">
            {supportScope.map((item) => {
              const Icon = item.Icon;

              return (
                <div className="support-scope-item" key={item.title}>
                  <span className="support-scope-icon">
                    <Icon size={22} strokeWidth={1.7} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="home-section process-section">
        <div className="container">
          <div className="section-head process-head">
            <span className="section-eyebrow">SUPPORT / PROCESS</span>
            <h2 className="section-h2">ご相談から施工・保守までの流れ</h2>
            <p className="section-lead">
              お問い合わせ後、内容を確認しながら順番に進めていきます。
            </p>
          </div>

          <div className="process-grid">
            {flow.map((item) => (
              <div className="process-card" key={item.step}>
                <div className="process-card-image">
                  <img src={item.image} alt="" loading="lazy" decoding="async" />
                </div>
                <div className="process-card-body">
                  <span className="process-number">{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section company-section company-proof-section" id="about">
        <div className="company-bg" aria-hidden="true" />

        <div className="container">
          <div className="section-head company-proof-head">
            <span className="section-eyebrow">COMPANY / LICENSE</span>
            <h2 className="section-h2">会社概要・許認可</h2>
            <p className="section-lead">
              株式会社エイチエスは、東京都・埼玉県を中心に、看板・LED・防犯カメラ・駐車場設備などの選定・製作・施工・保守に対応しています。
            </p>
          </div>

          <div className="company-proof-grid">
            <div className="company-proof-card">
              <span className="company-proof-label">CONSTRUCTION LICENSE</span>
              <h3>建設業許可</h3>
              <p>東京都知事 許可（般-5）第157570号</p>
              <small>鋼構造物工事業</small>
            </div>

            <div className="company-proof-card">
              <span className="company-proof-label">OUTDOOR AD REGISTRATION</span>
              <h3>屋外広告業登録</h3>
              <p>東京都・埼玉県・千葉県・神奈川県 ほか各自治体</p>
              <small>看板・サイン関連工事に対応</small>
            </div>

            <div className="company-proof-card">
              <span className="company-proof-label">OFFICE / FACTORY</span>
              <h3>川口工場・営業所</h3>
              <p>埼玉県川口市新堀835-2</p>
              <small>現場確認・製作・施工のご相談に対応</small>
            </div>
          </div>

          <div className="company-quick-links">
            <a href="/company#company-overview" className="company-quick-link">
              <Building2 size={20} strokeWidth={1.6} />
              <span>会社概要</span>
              <ArrowRight size={14} strokeWidth={2} />
            </a>

            <a href="/company#licenses" className="company-quick-link">
              <BadgeCheck size={20} strokeWidth={1.6} />
              <span>許認可一覧</span>
              <ArrowRight size={14} strokeWidth={2} />
            </a>

            <a href="/company#business" className="company-quick-link">
              <ShieldCheck size={20} strokeWidth={1.6} />
              <span>事業内容</span>
              <ArrowRight size={14} strokeWidth={2} />
            </a>

            <a href="/company#access" className="company-quick-link">
              <MapPinned size={20} strokeWidth={1.6} />
              <span>アクセス</span>
              <ArrowRight size={14} strokeWidth={2} />
            </a>
          </div>
        </div>
      </section>

      <section className="home-final-cta">
        <div className="container cta-container">
          <div className="cta-message">
            <Mail size={42} strokeWidth={1.35} />
            <div>
              <span className="section-eyebrow">CONTACT</span>
              <h2>ご相談・お見積り・資料請求はこちら</h2>
              <p>
                製品・サービスに関するご質問やご相談など、お気軽にお問い合わせください。
              </p>
              <Link to="/contact" className="cta-button">
                お問い合わせフォームへ <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="cta-contact-box">
            <a href="tel:0368250852">
              <Phone size={20} />
              <span>お電話でのお問い合わせ</span>
              <strong>03-6825-0852</strong>
            </a>
            <a href="mailto:kim@hs-i.co.jp">
              <Mail size={20} />
              <span>メールでのお問い合わせ</span>
              <strong>kim@hs-i.co.jp</strong>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;